import { Button, Modal, Select, Stack, Textarea, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Languages } from '@routellers/types/dist/shared/enum/languages.enum';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, apiErrorMessage } from '../api';
import { enumOptions } from '../i18n';

import { AuthorSelect } from './author-select';
import { CitySearchSelect } from './city-search-select';

type Props = {
    opened: boolean;
    onClose: () => void;
    onCreated: (questId: number) => void;
};

export const QuestCreateDialog = ({ opened, onClose, onCreated }: Props) => {
    const { t } = useTranslation();
    const languageOptions = useMemo(
        () => enumOptions(Object.values(Languages), t, 'enums.contentLanguage'),
        [t],
    );
    const [title, setTitle] = useState('');
    const [tagline, setTagline] = useState('');
    const [cityId, setCityId] = useState<number | null>(null);
    const [language, setLanguage] = useState<Languages>(Languages.EN);
    const [authorPartnerId, setAuthorPartnerId] = useState<number | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!opened) return;

        setTitle('');
        setTagline('');
        setCityId(null);
        setLanguage(Languages.EN);
        setAuthorPartnerId(null);
    }, [opened]);

    const create = async () => {
        if (!title.trim() || !cityId) {
            notifications.show({
                color: 'red',
                message: t('editors.questCreate.titleCityRequired'),
            });

            return;
        }

        setSaving(true);

        try {
            const { data } = await api.cityQuestsApi.create({
                data: {
                    cityId,
                    language,
                    title: title.trim(),
                    tagline: tagline.trim() || null,
                    createdByPartnerId: authorPartnerId,
                },
            });

            if (data?.id) {
                notifications.show({ color: 'green', message: t('editors.questCreate.created') });
                onCreated(data.id);
                onClose();
            }
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal opened={opened} onClose={onClose} title={t('editors.questCreate.title')} centered>
            <Stack gap='md'>
                <TextInput
                    label={t('editors.questCreate.titleField')}
                    required
                    value={title}
                    onChange={(e) => setTitle(e.currentTarget.value)}
                />
                <Textarea
                    label={t('editors.questCreate.tagline')}
                    minRows={2}
                    autosize
                    value={tagline}
                    onChange={(e) => setTagline(e.currentTarget.value)}
                />
                <CitySearchSelect value={cityId} onChange={setCityId} />
                <Select
                    label={t('editors.questCreate.originalLanguage')}
                    description={t('editors.questCreate.originalLanguageHint')}
                    data={languageOptions}
                    value={language}
                    onChange={(v) => v && setLanguage(v as Languages)}
                />
                <AuthorSelect
                    label={t('editors.questCreate.author')}
                    description={t('editors.questCreate.authorHint')}
                    value={authorPartnerId}
                    onChange={(author) => setAuthorPartnerId(author?.id ?? null)}
                />
                <Button loading={saving} onClick={create}>
                    {t('editors.questCreate.create')}
                </Button>
            </Stack>
        </Modal>
    );
};
